import React from 'react';
import { Box, Typography } from '@mui/material';
import { MoChip, MoSpacing } from '@motivo/guanyin/src/components';
import { ResultOf } from ':src/graphql';
import { userDetailsByIdQuery } from '../../graphql/queries';
import { formatDate } from '../../utils/dateFormatting';
import ZeusIncidentLink from '../shared/ZeusIncidentLink';
import { Section, SectionItem } from '../Section';

type User = NonNullable<ResultOf<typeof userDetailsByIdQuery>['userById']>;

interface IncidentsSectionProps {
  user: User;
}

export default function IncidentsSection({ user }: IncidentsSectionProps) {
  const incidentCount = user.supervisorIncidents.length;
  const strikeCount = user.supervisorIncidentStrikes.length;
  const totalCount = incidentCount + strikeCount;
  const hasIncidents = incidentCount > 0;
  const hasStrikes = strikeCount > 0;

  if (!hasIncidents && !hasStrikes) {
    return null;
  }

  return (
    <Section title="Incidents & strikes" count={totalCount > 0 ? totalCount : undefined}>
      <MoSpacing y={2}>
        {hasIncidents && (
          <SectionItem label="Incidents">
            <MoSpacing y={1.5}>
              {user.supervisorIncidents.map((incident) => (
                <Box
                  key={incident.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <ZeusIncidentLink incident={incident} />
                  {incident.incidentReportStatus && (
                    <MoChip label={incident.incidentReportStatus.name} color="gray" size="sm" />
                  )}
                </Box>
              ))}
            </MoSpacing>
          </SectionItem>
        )}

        {hasStrikes && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Strikes
            </Typography>
            <MoSpacing y={1.5}>
              {user.supervisorIncidentStrikes.map((strike) => (
                <Box key={strike.id}>
                  <Typography>
                    {formatDate(strike.createdAt)}
                    {strike.createdByAdmin && ` • ${strike.createdByAdmin.fullName}`}
                  </Typography>
                  {strike.comments && (
                    <Typography variant="caption" color="textSecondary">
                      {strike.comments}
                    </Typography>
                  )}
                </Box>
              ))}
            </MoSpacing>
          </Box>
        )}
      </MoSpacing>
    </Section>
  );
}
